import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CurrencyDollar, Warning, Clock, Camera, MapPinLine, Users, TrendUp, Bank, ShieldWarning, CaretRight, CheckCircle } from "@phosphor-icons/react";
import client from "../lib/api";
import { money } from "../lib/helpers";

const QUEUE_META = {
  unpaid_overdue: { icon: Warning, view: "billing" },
  overdue_payouts: { icon: Bank, view: "pay" },
  deliverables_unpaid: { icon: Clock, view: "billing" },
  ungraded: { icon: Camera, view: null },
  unassigned: { icon: MapPinLine, view: null },
};

function countdown(dateStr, now) {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return null;
  const past = t - now < 0;
  let diff = Math.abs(t - now);
  const dd = Math.floor(diff / 86400000);
  const hh = Math.floor((diff % 86400000) / 3600000);
  const mm = Math.floor((diff % 3600000) / 60000);
  const core = dd > 0 ? `${dd}d ${hh}h` : hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
  return { text: past ? `${core} ago` : `in ${core}`, urgent: !past && (t - now) < 86400000, past };
}

const KPI_TONES = {
  emerald: { cls: "surface-kpi--emerald", medal: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30", value: "#1A4331" },
  crimson: { cls: "surface-kpi--crimson", medal: "bg-[#F0E6E6] text-[#7A0000] border-[#7E1119]/30", value: "#7A0000" },
  bronze: { cls: "surface-kpi--bronze", medal: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35", value: "#8A5028" },
  sapphire: { cls: "surface-kpi--sapphire", medal: "bg-[#E6EAEF] text-[#12233D] border-[#2B4C7E]/30", value: "#12233D" },
  oxblood: { cls: "surface-kpi--oxblood-accent", medal: "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/20", value: "#4A0000" },
};

const Kpi = ({ label, value, sub, icon: Icon, variant = "oxblood", testid }) => {
  const t = KPI_TONES[variant] || KPI_TONES.oxblood;
  return (
    <div data-testid={testid} className={`surface-kpi ${t.cls} p-6 flex flex-col gap-3`}>
      <div className="flex items-center justify-between relative z-10">
        <p className="eyebrow text-[#B08D57]">{label}</p>
        <span className={`h-9 w-9 rounded-full flex items-center justify-center border ${t.medal}`}><Icon size={18} weight="duotone" /></span>
      </div>
      <p className="font-display text-[1.5rem] sm:text-[2rem] lg:text-[2.6rem] font-black leading-[0.95] tracking-tight tabular-nums truncate relative z-10" style={{ color: t.value }}>{value}</p>
      {sub && <p className="text-xs text-[#7D6060] relative z-10">{sub}</p>}
    </div>
  );
};

export default function CommandCenter({ onOpenProperty, onGo }) {
  const [d, setD] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let live = true;
    const load = () => client.get("/admin/command-center").then((r) => live && setD(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => { live = false; clearInterval(iv); clearInterval(tick); };
  }, []);

  if (!d) return <div className="py-24 text-center text-[#9A968E] text-sm" data-testid="command-loading">Loading command center…</div>;
  const k = d.kpis;
  const memberTotal = Object.values(k.members || {}).reduce((a, b) => a + b, 0);

  const jump = (item, view) => {
    if (item.kind === "shoot" || item.kind === "payout") {
      if (item.property_id && onOpenProperty) return onOpenProperty(item.property_id);
    }
    if (view && onGo) onGo(view);
    else if (item.property_id && onOpenProperty) onOpenProperty(item.property_id);
  };

  return (
    <div className="space-y-8" data-testid="command-center">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-collected" label={`Collected · ${k.month_label}`} value={money(k.ar_collected_month)} icon={TrendUp} variant="emerald" sub="Paid invoices this month" />
        <Kpi testid="kpi-ar_outstanding" label="Outstanding AR" value={money(k.ar_outstanding)} icon={CurrencyDollar} variant="crimson" sub={`${money(k.ar_overdue)} overdue`} />
        <Kpi testid="kpi-ap_outstanding" label="Payouts owed" value={money(k.ap_outstanding)} icon={Bank} variant="bronze" sub="Unpaid crew payables" />
        <Kpi testid="kpi-active_shoots" label="Active shoots" value={k.active_shoots} icon={Camera} variant="sapphire" sub={`${k.completed_month} completed in ${k.month_label}`} />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <ShieldWarning size={18} weight="duotone" className="text-[#7E1119]" />
          <h3 className="font-display text-xl text-[#2C2626]">Needs attention</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(d.queues)
            .sort((a, b) => (a[0] === "unassigned" ? -1 : b[0] === "unassigned" ? 1 : 0))
            .map(([key, q], i) => {
            const meta = QUEUE_META[key] || {};
            const Icon = meta.icon || Warning;
            return (
              <motion.div key={key} data-testid={`queue-${key}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className={`${q.count > 0 ? "surface-alert" : "surface-kpi"} overflow-hidden ${key === "unassigned" ? "lg:col-span-2" : ""}`}>
                <div className="flex items-center gap-3 px-5 py-3.5 border-b border-[#F0ECE2]">
                  <span className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${q.tone}14`, color: q.tone }}><Icon size={18} weight="duotone" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1A1715]">{q.label}</p>
                    {q.total != null && q.count > 0 && <p className="text-xs text-[#8A8681]">{money(q.total)} total</p>}
                  </div>
                  <span data-testid={`queue-${key}-count`} className="text-2xl font-display tabular-nums" style={{ color: q.count > 0 ? q.tone : "#B7B2A8" }}>{q.count}</span>
                </div>
                {q.count === 0 ? (
                  <p className="px-5 py-6 text-center text-sm text-[#9A968E] flex items-center justify-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#2E5D34]" /> All clear</p>
                ) : (
                  <ul className="divide-y divide-[#FAF9F6] max-h-64 overflow-y-auto reelty-scroll">
                    {q.items.map((it, idx) => (
                      <li key={idx}>
                        <button data-testid={`queue-${key}-item-${idx}`} onClick={() => jump(it, meta.view)}
                          className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[#FAF9F6]/60 transition-colors group">
                          <span className="flex-1 min-w-0 flex flex-col leading-tight">
                            <span className="text-sm text-[#2C2626] truncate">{it.agent || it.title}</span>
                            {(it.address || it.agent) && <span className="text-[11px] text-[#9A968E] truncate">{it.agent ? (it.address || it.title) : it.address}</span>}
                          </span>
                          {it.amount != null && <span className="text-sm font-semibold text-[#4A0000] tabular-nums">{money(it.amount)}</span>}
                          {it.days_overdue != null && it.days_overdue > 0 && <span className="text-[11px] font-semibold text-[#7E1119]">{it.days_overdue}d late</span>}
                          {it.missing && <span className="text-[11px] text-[#8A8681] capitalize">needs {it.missing}</span>}
                          {(() => {
                            const c = it.shoot_date ? countdown(it.shoot_date, now) : null;
                            if (!c) return null;
                            return (
                              <span data-testid={`queue-${key}-countdown-${idx}`}
                                className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums rounded-[4px] px-1.5 py-0.5 border ${c.past ? "text-[#9A968E] border-[#E9DDC6]" : c.urgent ? "text-[#7E1119] border-[#7E1119]/30 bg-[#7E1119]/[0.06]" : "text-[#8A6D1B] border-[#B08D57]/35 bg-[#B08D57]/[0.08]"}`}>
                                <Clock size={11} weight="fill" />{c.text}
                              </span>
                            );
                          })()}
                          {it.photographer && !it.missing && <span className="text-[11px] text-[#8A8681] truncate max-w-[120px] hidden sm:inline">{it.photographer}</span>}
                          <CaretRight size={14} className="text-[#C9C4BA] group-hover:text-[#4A0000] transition-colors shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button data-testid="cc-flag-overrides" onClick={() => onGo && onGo("oversight")}
          className="surface-kpi p-4 flex items-center gap-3 text-left">
          <span className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-[#7E1119]/10 text-[#7E1119]"><ShieldWarning size={20} weight="duotone" /></span>
          <div className="min-w-0"><p className="text-xl sm:text-2xl font-display tabular-nums text-[#1A1715] leading-none">{d.flags.override_offenders}</p><p className="text-[#8A8681] mt-1 leading-snug break-words" style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.35vw, 0.8125rem)" }}>Check-in override repeat offenders</p></div>
        </button>
        <button data-testid="cc-flag-payout-setup" onClick={() => onGo && onGo("pay")}
          className="surface-kpi p-4 flex items-center gap-3 text-left">
          <span className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-[#B08D57]/12 text-[#B08D57]"><Bank size={20} weight="duotone" /></span>
          <div className="min-w-0"><p className="text-xl sm:text-2xl font-display tabular-nums text-[#1A1715] leading-none">{d.flags.payout_setup_missing}</p><p className="text-[#8A8681] mt-1 leading-snug break-words" style={{ fontSize: "clamp(0.6875rem, 0.6rem + 0.35vw, 0.8125rem)" }}>Crew missing payout setup</p></div>
        </button>
        <div className="surface-kpi p-4 flex items-center gap-3">
          <span className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-[#4A0000]/8 text-[#4A0000]"><Users size={20} weight="duotone" /></span>
          <div className="min-w-0"><p className="text-xl sm:text-2xl font-display tabular-nums text-[#1A1715] leading-none">{memberTotal}</p><p className="text-[11px] sm:text-xs text-[#8A8681] mt-1">Active members</p></div>
        </div>
      </div>
    </div>
  );
}
